import { IPublicClientApplication } from '@azure/msal-browser';
import { AnyAction, Dispatch, Middleware, MiddlewareAPI } from '@reduxjs/toolkit';
import { ChatProviderType } from '../../domain/chat/models/ChatProviderType';
import { AuthorRoles, ChatMessage, ChatMessageType } from '../../domain/messages/models/ChatMessage';
import { appendMessages, removeMessage, updateCitations, updateMessageContent } from '../features/chatMessages/chatMessagesSlice';
import { updateSessionTitle } from '../features/chatSessions/chatSessionsSlice';
import { setBotResponseStatus, setBulkStatusMessageId, setIsResponseGenerating, setStreamingMessageId } from '../features/chatUi/chatUiSlice';
import { FatalConnectionError, SseConnectionManager, SseEvent } from '../../services/message-relay/sseConnectionManager';
import { RootState } from '../store';
import { trackEvent, trackServiceException } from '../../app/telemetry/appInsights';
import { ChatCitation } from '../../domain/messages/models/ChatCitation';

type StoreAPI = MiddlewareAPI<Dispatch<AnyAction>, RootState>;

function getChannelsForProviderType(providerType: ChatProviderType): string[] {
    switch (providerType) {
        case ChatProviderType.Nexus:
            return ['nexus'];
        case ChatProviderType.ProGPT:
        default:
            return ['progpt'];
    }
}

export function createSseMiddleware(msalInstance: IPublicClientApplication): Middleware {
    return (store: StoreAPI) => {
        const sseManager = new SseConnectionManager(msalInstance);

        const activeConnections = new Map<string, Set<string>>();
        const contentAccumulator = new Map<string, string>();
        const chunkBuffers = new Map<string, Map<number, string>>();
        const localStreamingIds = new Map<string, string>();
        const streamKeysBySession = new Map<string, Set<string>>();
        const bulkMessageIdByStreamKey = new Map<string, string>();
        const bulkContentByStreamKey = new Map<string, string>();
        const connectionEpochs = new Map<string, number>();
        const typingTimers = new Map<string, ReturnType<typeof setInterval>>();
        const typingTargets = new Map<string, string>();
        const typingSessionByMessageId = new Map<string, string>();
        const isBulkStatusMessageId = (messageId?: string): messageId is string =>
            typeof messageId === 'string' && messageId.startsWith('bulk_status_');

        const findNestedStreamKey = (obj: any, candidateNames: string[]): string | undefined => {
            if (!obj || typeof obj !== 'object') return undefined;

            for (const [key, value] of Object.entries(obj)) {
                if (candidateNames.includes(key) && typeof value === 'string' && value.trim().length > 0) {
                    return value;
                }

                if (typeof value === 'object') {
                    const nestedKey = findNestedStreamKey(value, candidateNames);
                    if (nestedKey) return nestedKey;
                }
            }

            return undefined;
        };

        const getStreamKey = (event: SseEvent, sessionId: string): string => {
            const candidateKeys = [
                event.messageId,
                event.data?.messageId,
                event.data?.id,
                event.data?.responseId,
                event.data?.response?.id,
                event.data?.message?.id,
                event.data?.message?.messageId,
                event.data?.message?.runId,
                event.data?.runId,
                event.runId,
            ];

            for (const candidate of candidateKeys) {
                if (typeof candidate === 'string' && candidate.trim().length > 0) {
                    return candidate;
                }
            }

            const nestedCandidate = findNestedStreamKey(event, ['messageId', 'runId', 'responseId', 'id']);
            return typeof nestedCandidate === 'string' && nestedCandidate.trim().length > 0 ? nestedCandidate : sessionId;
        };

        const registerStreamKey = (sessionId: string, streamKey: string): void => {
            const streamKeys = streamKeysBySession.get(sessionId) ?? new Set<string>();
            streamKeys.add(streamKey);
            streamKeysBySession.set(sessionId, streamKeys);
        };

        const getSessionStreamKeys = (sessionId: string): string[] => [
            sessionId,
            ...(streamKeysBySession.get(sessionId) ?? []),
        ];

        const getSessionStreamingId = (sessionId: string): string | undefined => {
            for (const streamKey of getSessionStreamKeys(sessionId)) {
                const streamingId = localStreamingIds.get(streamKey);
                if (streamingId) return streamingId;
            }

            return undefined;
        };

        const getSessionAccumulatedContent = (sessionId: string): string => {
            for (const streamKey of getSessionStreamKeys(sessionId)) {
                const content = contentAccumulator.get(streamKey);
                if (content) return content;
            }

            return '';
        };

        const getMessageContent = (sessionId: string, messageId: string): string | undefined => {
            const messages = store.getState().chatMessages.bySessionId[sessionId] ?? [];
            return messages.find((message) => message.id === messageId)?.content;
        };

        const clearTypingTimer = (messageId: string): void => {
            const timer = typingTimers.get(messageId);
            if (timer) {
                clearInterval(timer);
                typingTimers.delete(messageId);
            }
            typingTargets.delete(messageId);
            typingSessionByMessageId.delete(messageId);
        };

        const clearTypingForSession = (sessionId: string): void => {
            const messageIds = [...typingSessionByMessageId.entries()]
                .filter(([, ownerSessionId]) => ownerSessionId === sessionId)
                .map(([messageId]) => messageId);

            for (const messageId of messageIds) {
                clearTypingTimer(messageId);
            }
        };

        const flushTypingForSession = (sessionId: string): void => {
            const messageIds = [...typingSessionByMessageId.entries()]
                .filter(([, ownerSessionId]) => ownerSessionId === sessionId)
                .map(([messageId]) => messageId);

            for (const messageId of messageIds) {
                const latestTarget = typingTargets.get(messageId);
                if (latestTarget === undefined) continue;
                const currentContent = getMessageContent(sessionId, messageId) ?? '';
                if (currentContent !== latestTarget) {
                    store.dispatch(updateMessageContent({ sessionId, messageId, content: latestTarget }));
                }
            }
        };

        const animateMessageToTarget = (sessionId: string, messageId: string, targetContent: string): void => {
            typingTargets.set(messageId, targetContent);
            typingSessionByMessageId.set(messageId, sessionId);

            if (typingTimers.has(messageId)) {
                return;
            }

            const tickMs = 22;
            const charsPerTick = 4;

            const timer = setInterval(() => {
                const latestTarget = typingTargets.get(messageId);
                if (latestTarget === undefined) {
                    clearTypingTimer(messageId);
                    return;
                }

                const currentContent = getMessageContent(sessionId, messageId) ?? '';
                if (currentContent.length >= latestTarget.length) {
                    if (currentContent !== latestTarget) {
                        store.dispatch(updateMessageContent({ sessionId, messageId, content: latestTarget }));
                    }
                    clearTypingTimer(messageId);
                    return;
                }

                const nextLength = Math.min(currentContent.length + charsPerTick, latestTarget.length);
                const nextContent = latestTarget.slice(0, nextLength);
                store.dispatch(updateMessageContent({ sessionId, messageId, content: nextContent }));
            }, tickMs);

            typingTimers.set(messageId, timer);
        };


        const canReuseStreamingMessage = (sessionId: string, streamKey: string, messageId: string): boolean => {
            if (contentAccumulator.has(streamKey)) {
                return true;
            }

            return (getMessageContent(sessionId, messageId) ?? '') === '';
        };

        const clearSessionStreamingState = (sessionId: string): void => {
            clearTypingForSession(sessionId);

            const sessionStreamKeys = getSessionStreamKeys(sessionId);
            for (const streamKey of sessionStreamKeys) {
                contentAccumulator.delete(streamKey);
                chunkBuffers.delete(streamKey);
                localStreamingIds.delete(streamKey);
            }

            streamKeysBySession.delete(sessionId);
            for (const [streamKey] of bulkMessageIdByStreamKey) {
                if (streamKey === `bulk:${sessionId}` || streamKey.startsWith(`bulk:${sessionId}:`)) {
                    bulkMessageIdByStreamKey.delete(streamKey);
                    bulkContentByStreamKey.delete(streamKey);
                }
            }
        };

        const connectToSession = async (sessionId: string): Promise<void> => {
            const state = store.getState();
            const session = state.chatSessions.byId[sessionId];
            if (!session) return;

            const channels = getChannelsForProviderType(session.providerType);

            // Guard: already connected with same channels
            const existing = activeConnections.get(sessionId);
            if (existing && channels.every((c) => existing.has(c))) return;

            activeConnections.set(sessionId, new Set(channels));

            const endpoint = process.env.REACT_APP_SSE_ENDPOINT;
            if (!endpoint) throw new Error('SSE endpoint is not configured');

            const capturedEpoch = connectionEpochs.get(sessionId) ?? 0;

            try {
                await sseManager.connect({
                    endpoint,
                    threadId: sessionId,
                    chatId: sessionId,
                    channels,
                    onMessage: (event) => {
                        if (connectionEpochs.get(sessionId) !== capturedEpoch) return;
                        handleSseEvent(event, sessionId);
                    },
                    onError: (error) => {
                        if (connectionEpochs.get(sessionId) !== capturedEpoch) return;
                        handleSseError(error, sessionId);
                    },
                });
            } catch (error) {
                trackServiceException(error, {
                    source: 'SseMiddleware.connectToSession',
                    operation: 'connect',
                    sessionId,
                    isFatal: true,
                }, {
                    channels: channels.join(','),
                });
                activeConnections.delete(sessionId);
            }
        };

        const disconnectFromSession = (sessionId: string): void => {
            // Always clear streaming state, regardless of whether the connection is tracked
            clearSessionStreamingState(sessionId);

            const channels = activeConnections.get(sessionId);
            if (!channels) return;
            const connectionKey = sseManager.buildConnectionKey({ chatId: sessionId, channels: [...channels] });
            sseManager.disconnect(connectionKey);
            activeConnections.delete(sessionId);
        };

        const finalizeSession = (sessionId: string): void => {
            flushTypingForSession(sessionId);

            // If no content was ever streamed, remove the placeholder bubble
            const accumulatedContent = getSessionAccumulatedContent(sessionId);
            const placeholderId = getSessionStreamingId(sessionId);
            if (placeholderId && accumulatedContent === '') {
                clearTypingTimer(placeholderId);
                store.dispatch(removeMessage({ sessionId, messageId: placeholderId }));
            }

            disconnectFromSession(sessionId);
            store.dispatch(setStreamingMessageId({ sessionId, value: null }));
            store.dispatch(setBotResponseStatus({ sessionId, value: '' }));
            store.dispatch(setIsResponseGenerating({ sessionId, value: false }));
        };

        const mapToChatCitation = (citations: unknown): ChatCitation[] => {
            if (!citations || !Array.isArray(citations)) return [];
            const formattedCitations: ChatCitation[] = citations.reduce<ChatCitation[]>((accumulator, item) => {
                var title: string = (item.title && typeof item.title === 'string') && !item.SourceName ? item.title : item.SourceName
                var link: string = (item.url && typeof item.url === 'string') && !item.Link ? item.url : item.Link
                var agentName: string = (item.agentName && typeof item.agentName === 'string') ? item.agentName : null;
                accumulator.push({ title: title, link: link, agentName: agentName });
                return accumulator;
            }, []);
            return formattedCitations.length > 0 ? formattedCitations : [];
        }

        const handleSseEvent = (event: SseEvent, sessionId: string): void => {
            // Assistants events carry threadId in the payload; nexus/progpt do not
            const resolvedSessionId = event.threadId ?? sessionId;
            const streamKey = getStreamKey(event, resolvedSessionId);
            const sessionUiState = store.getState().chatUi.bySessionId[resolvedSessionId];
            const isBulkProcessingOnly =
                sessionUiState?.isBulkUploadProcessing === true && sessionUiState?.isResponseGenerating !== true;

            switch (event.type) {
                case 'message.delta':
                case 'message': {
                    // Bulk prompt processing can emit multiple independent responses over one SSE session.
                    // In this mode, bind each response stream to a dedicated message block.
                    if (isBulkProcessingOnly) {
                        const incoming = event.content ?? '';
                        const bulkStreamKey = `bulk:${resolvedSessionId}:${streamKey}`;
                        let existingBulkMessageId = bulkMessageIdByStreamKey.get(bulkStreamKey);

                        registerStreamKey(resolvedSessionId, streamKey);

                        const hasExistingContent = (bulkContentByStreamKey.get(bulkStreamKey) ?? '').length > 0;
                        const shouldStartNewBulkMessage = existingBulkMessageId && event.chunkIndex === 0 && hasExistingContent;
                        if (shouldStartNewBulkMessage) {
                            bulkMessageIdByStreamKey.delete(bulkStreamKey);
                            bulkContentByStreamKey.delete(bulkStreamKey);
                            existingBulkMessageId = undefined;
                        }

                        if (!existingBulkMessageId && incoming.trim().length > 0) {
                            const bulkStatusMessageId = store.getState().chatUi.bySessionId[resolvedSessionId]?.bulkStatusMessageId;
                            if (isBulkStatusMessageId(bulkStatusMessageId)) {
                                clearTypingTimer(bulkStatusMessageId);
                                store.dispatch(removeMessage({ sessionId: resolvedSessionId, messageId: bulkStatusMessageId }));
                                store.dispatch(setBulkStatusMessageId({ sessionId: resolvedSessionId, value: null }));
                                store.dispatch(setBotResponseStatus({ sessionId: resolvedSessionId, value: '' }));
                            }

                            const bulkMessageId = `bulk_${resolvedSessionId}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
                            const botMessage: ChatMessage = {
                                chatId: resolvedSessionId,
                                type: ChatMessageType.Message,
                                timestamp: Date.now(),
                                userName: 'Assistant',
                                content: incoming,
                                authorRole: AuthorRoles.Bot,
                                id: bulkMessageId,
                            };
                            bulkMessageIdByStreamKey.set(bulkStreamKey, bulkMessageId);
                            bulkContentByStreamKey.set(bulkStreamKey, incoming);
                            store.dispatch(appendMessages({ sessionId: resolvedSessionId, messages: [botMessage] }));
                        } else if (existingBulkMessageId) {
                            const current = bulkContentByStreamKey.get(bulkStreamKey) ?? getMessageContent(resolvedSessionId, existingBulkMessageId) ?? '';
                            const next = `${current}${incoming}`;
                            bulkContentByStreamKey.set(bulkStreamKey, next);
                            store.dispatch(updateMessageContent({ sessionId: resolvedSessionId, messageId: existingBulkMessageId, content: next }));
                        }

                        break;
                    }

                    registerStreamKey(resolvedSessionId, streamKey);

                    let newContent: string;
                    if (event.chunkIndex !== undefined) {
                        const buffer = chunkBuffers.get(streamKey) ?? new Map<number, string>();
                        buffer.set(event.chunkIndex, event.content ?? '');
                        chunkBuffers.set(streamKey, buffer);
                        newContent = [...buffer.entries()]
                            .sort(([a], [b]) => a - b)
                            .map(([, v]) => v)
                            .join('');
                    } else {
                        newContent = (contentAccumulator.get(streamKey) ?? '') + (event.content ?? '');
                    }
                    contentAccumulator.set(streamKey, newContent);

                    const streamStreamingId = localStreamingIds.get(streamKey);
                    const sessionStreamingId =
                        streamKey !== resolvedSessionId ? localStreamingIds.get(resolvedSessionId) : undefined;
                    const existingStreamingId =
                        streamStreamingId && canReuseStreamingMessage(resolvedSessionId, streamKey, streamStreamingId)
                            ? streamStreamingId
                            : sessionStreamingId && canReuseStreamingMessage(resolvedSessionId, resolvedSessionId, sessionStreamingId)
                                ? sessionStreamingId
                                : undefined;

                    if (existingStreamingId) {
                        localStreamingIds.set(streamKey, existingStreamingId);
                        // Update the existing placeholder/streaming bubble in place
                        animateMessageToTarget(resolvedSessionId, existingStreamingId, newContent);
                    } else {
                        // Fallback: no placeholder was pre-created (shouldn't happen in normal flow)
                        const streamingId = `pending_${resolvedSessionId}_${Date.now()}`;
                        const botMessage: ChatMessage = {
                            chatId: resolvedSessionId,
                            type: ChatMessageType.Message,
                            timestamp: Date.now(),
                            userName: 'Assistant',
                            content: newContent,
                            authorRole: AuthorRoles.Bot,
                            id: streamingId,
                        };
                        localStreamingIds.set(streamKey, streamingId);
                        store.dispatch(appendMessages({ sessionId: resolvedSessionId, messages: [botMessage] }));
                        store.dispatch(setStreamingMessageId({ sessionId: resolvedSessionId, value: streamingId }));
                    }
                    store.dispatch(setBotResponseStatus({ sessionId: resolvedSessionId, value: '' }));
                    break;
                }
                case 'citations': {
                    if (!event.citations) {
                        console.error('SSE citations failure. Found no citations despite citations event.');
                        break;
                    }
                    var newContent: ChatCitation[] = mapToChatCitation(event.citations);
                    const existingStreamingId = localStreamingIds.get(resolvedSessionId);
                    if (existingStreamingId) {
                        store.dispatch(
                            updateCitations({ sessionId: resolvedSessionId, messageId: existingStreamingId, content: newContent })
                        );
                    }
                    break;
                }

                case 'agent.status': {
                    const status = event.agentStatus ?? (event.data as { agentStatus?: string } | undefined)?.agentStatus ?? '';
                    store.dispatch(setBotResponseStatus({ sessionId: resolvedSessionId, value: status }));
                    break;
                }

                case 'system': {
                    const status = event.content ?? '';
                    store.dispatch(setBotResponseStatus({ sessionId: resolvedSessionId, value: status }));
                    break;
                }

                case 'message.completed': {
                    // Assistants only: SSE signals stream is done
                    finalizeSession(resolvedSessionId);
                    break;
                }
                case 'title.updated': {
                    const title = event.title ?? '';
                    if (!title) break;
                    store.dispatch(updateSessionTitle({
                        sessionId: resolvedSessionId,
                        title,
                        isTitleGenerated: true,
                    }));
                    break;
                }

                case 'run.failed': {
                    const errorMsg =
                        event.error ??
                        (event.data as { error?: string; errorMessage?: string } | undefined)?.error ??
                        (event.data as { error?: string; errorMessage?: string } | undefined)?.errorMessage ??
                        'Unknown error';
                    console.error('[SSE] run.failed:', errorMsg);
                    trackServiceException(new Error(errorMsg), {
                        source: 'SseMiddleware.handleSseEvent',
                        operation: 'run.failed',
                        sessionId: resolvedSessionId,
                        isFatal: true,
                    }, {
                        eventType: event.type,
                        runId: event.runId ?? '',
                    });
                    finalizeSession(resolvedSessionId);
                    break;
                }

                case 'connection':
                    break;

                default:
                    break;
            }
        };

        const handleSseError = (error: Error, sessionId: string): void => {
            const msg = error.message ?? '';
            if (error instanceof FatalConnectionError) {
                console.error('[SSE] Fatal connection error:', msg);
                trackServiceException(error, {
                    source: 'SseMiddleware.handleSseError',
                    operation: 'stream',
                    sessionId,
                    isFatal: true,
                });
                finalizeSession(sessionId);
            } else {
                console.warn('[SSE] Connection error:', msg);
                trackEvent('sse.session.retrying', {
                    source: 'SseMiddleware.handleSseError',
                    sessionId,
                    message: msg,
                });
            }
        };

        return (next: Dispatch) => (action: AnyAction) => {
            const result = next(action);

            if (action.type === 'chatUi/setIsResponseGenerating') {
                const { sessionId, value, fallbackContent, wasAborted } = action.payload as { sessionId: string; value: boolean; fallbackContent?: string; wasAborted?: boolean };

                if (value) {
                    // Remove any orphaned placeholder from a previous in-flight response
                    // before tearing down the connection (needs the localStreamingId still set)
                    const oldPlaceholderId = getSessionStreamingId(sessionId);
                    const oldContent = getSessionAccumulatedContent(sessionId);
                    if (oldPlaceholderId && oldContent === '') {
                        clearTypingTimer(oldPlaceholderId);
                        store.dispatch(removeMessage({ sessionId, messageId: oldPlaceholderId }));
                    }

                    // Bump the epoch so any late callbacks from an old connection are ignored
                    const epoch = (connectionEpochs.get(sessionId) ?? 0) + 1;
                    connectionEpochs.set(sessionId, epoch);

                    // Tear down any in-flight connection so the new one isn't blocked
                    // by the activeConnections guard in connectToSession
                    disconnectFromSession(sessionId);

                    // Immediately add a placeholder bot bubble so the pending dots appear
                    // inside a real message bubble rather than as a separate component
                    const placeholderId = `pending_${sessionId}_${Date.now()}`;
                    const placeholderMessage: ChatMessage = {
                        chatId: sessionId,
                        type: ChatMessageType.Message,
                        timestamp: Date.now(),
                        userName: 'Assistant',
                        content: '',
                        authorRole: AuthorRoles.Bot,
                        id: placeholderId,
                    };
                    localStreamingIds.set(sessionId, placeholderId);
                    contentAccumulator.set(sessionId, '');
                    registerStreamKey(sessionId, sessionId);
                    chunkBuffers.delete(sessionId);
                    store.dispatch(appendMessages({ sessionId, messages: [placeholderMessage] }));
                    store.dispatch(setStreamingMessageId({ sessionId, value: placeholderId }));

                    connectToSession(sessionId).catch(console.error);
                } else {
                    const accumulatedContent = getSessionAccumulatedContent(sessionId);
                    const placeholderId = getSessionStreamingId(sessionId);
                    if (wasAborted) {
                        if (placeholderId && accumulatedContent !== '') {
                            store.dispatch(updateMessageContent({ sessionId, messageId: placeholderId, content: `${accumulatedContent}\n\n*(stopped)*` }));
                        } else if (placeholderId && fallbackContent) {
                            store.dispatch(updateMessageContent({ sessionId, messageId: placeholderId, content: `${fallbackContent}\n\n*(stopped)*` }));
                        } else if (placeholderId) {
                            store.dispatch(updateMessageContent({ sessionId, messageId: placeholderId, content: '*(stopped)*' }));
                        }
                    } else if (placeholderId && accumulatedContent === '' && fallbackContent) {
                        store.dispatch(updateMessageContent({ sessionId, messageId: placeholderId, content: fallbackContent }));
                    } else if (placeholderId && accumulatedContent === '') {
                        store.dispatch(removeMessage({ sessionId, messageId: placeholderId }));
                    }

                    disconnectFromSession(sessionId);
                    store.dispatch(setStreamingMessageId({ sessionId, value: null }));
                    store.dispatch(setBotResponseStatus({ sessionId, value: '' }));
                }
            }

            if (action.type === 'chatUi/setIsBulkUploadProcessing') {
                const { sessionId, value } = action.payload as { sessionId: string; value: boolean };

                if (value) {
                    bulkMessageIdByStreamKey.delete(`bulk:${sessionId}`);
                    bulkContentByStreamKey.delete(`bulk:${sessionId}`);
                    const epoch = connectionEpochs.get(sessionId) ?? 0;
                    connectionEpochs.set(sessionId, epoch === 0 ? 1 : epoch);
                    connectToSession(sessionId).catch(console.error);
                } else {
                    bulkMessageIdByStreamKey.delete(`bulk:${sessionId}`);
                    bulkContentByStreamKey.delete(`bulk:${sessionId}`);
                    const state = store.getState();
                    const sessionUiState = state.chatUi.bySessionId[sessionId];

                    if (!sessionUiState?.isResponseGenerating) {
                        disconnectFromSession(sessionId);
                        store.dispatch(setBotResponseStatus({ sessionId, value: '' }));
                    }
                }
            }

            return result;
        };
    };
}
